/**
 * Avatar circular con iniciales, y sus dos parientes del maquetado: la miniatura cuadrada de
 * inmueble (gradiente arena) y el rectángulo de documento.
 *
 * Fase 1: no hay fotos ni ficheros, así que ninguno carga una imagen. Cuando la fase 2 añada
 * almacenamiento, la imagen entra aquí y ninguna pantalla cambia.
 */
import styles from './Avatar.module.css';

interface AvatarProps {
  initials: string;
  size?: number;
  fontSize?: number;
}

export function Avatar({ initials, size = 48, fontSize }: AvatarProps) {
  return (
    <div
      className={styles.avatar}
      style={{
        width: size,
        height: size,
        fontSize: fontSize ?? Math.round(size * 0.35),
      }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
}

/** Miniatura de inmueble. Radio de control (6 px) salvo que se pida otro. */
/**
 * Miniatura de un inmueble.
 *
 * Antes era SIEMPRE un cuadrado beige: el marcador de posición del maquetado, que se quedó
 * puesto aunque hubiera fotos subidas. Ahora pinta la portada cuando la hay.
 *
 * `placeholderColor` es el color medio de la foto, calculado al subirla. Se pinta de fondo
 * mientras la imagen viaja, así la fila no parpadea en blanco y luego en color.
 */
export function Thumbnail({
  size = 46,
  radius = 6,
  src,
  placeholderColor,
  alt,
}: {
  size?: number;
  radius?: number;
  src?: string | null;
  placeholderColor?: string | null;
  alt?: string;
}) {
  return (
    <div
      className={styles.thumb}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        flexBasis: size,
        ...(placeholderColor ? { background: placeholderColor } : {}),
      }}
      // Sin foto no aporta nada al lector de pantalla: el nombre del inmueble va al lado.
      aria-hidden={src ? undefined : 'true'}
    >
      {src ? (
        <img
          src={src}
          alt={alt ?? ''}
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          className={styles.thumbImage}
          style={{ borderRadius: radius }}
          /*
           * Si la imagen no llega, se esconde y queda el color medio de fondo. La URL va
           * firmada y caduca a los 15 minutos: una pestaña abierta toda la mañana acabaría
           * enseñando el icono de imagen rota de Chrome en cada fila.
           */
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
      ) : null}
    </div>
  );
}

/** Marcador de posición de la tabla de Documentos. */
export function DocumentThumb() {
  return <div className={styles.doc} aria-hidden="true" />;
}
